import React from "react"
import { removeHtmlSuffixFromAnchors } from "@/utils/helper"
import { onFaqDescriptionLinkClick } from "@/components/DesignSystem/utils/faqLinkHandler"
import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"

/**
 * Map an AEM FAQ container into props consumable by the `FAQ` component.
 *
 * Extracts the teaser description and accordion items and returns an object
 * shaped for the design-system `FAQ` component. Returns `null` for empty input.
 *
 * @param {Object} data - The AEM container data (may include `:items`, teaser, accordion)
 * @return {Object|null} Mapped props for the `FAQ` component, or `null` when input is falsy
 */
export function mapFAQProps(data) {
  if (!data) return null

  const accordion = data?.[":items"]?.accordion || {}
  const accordionProps = accordion?.accordionProperties || {}
  const accordionItems = accordionProps[":items"] || {}
  const itemsOrder =
    accordionProps[":itemsOrder"] || Object.keys(accordionItems)
  const singleExpansion = accordionProps.singleExpansion ?? true
  const expandedItems = accordionProps.expandedItems || []
  const defaultExpandedIndices = expandedItems
    .map(key => itemsOrder.indexOf(key))
    .filter(i => i !== -1)

  const mod = data.appliedCssClassNames
    ? data.appliedCssClassNames.match(/\bbg-([a-z]+)\b/)
    : null

  // Rewrite anchor hrefs that contain the internal link path using modifyHref
  const processDescriptionHrefs = (html = "") =>
    html.replace(
      /(<a\s[^>]*href=["'])([^"']*)/gi,
      (match, prefix, href) => `${prefix}${modifyHref(href)}`
    )

  // Clean HTML → plain text (basic)
  const stripHTML = (html = "") => html.replace(/<[^>]*>/g, "").trim()

  return {
    mod: mod ? mod[1] : null,
    showHeader: accordion?.title ? true : false,

    heading: {
      text: stripHTML(accordion?.title) || "",
    },

    description: {
      // Provide a React element as `text` so consumers can render it directly.
      // The element uses `dangerouslySetInnerHTML` to preserve markup.
      text: React.createElement("div", {
        dangerouslySetInnerHTML: {
          __html: processDescriptionHrefs(
            removeHtmlSuffixFromAnchors(accordion?.description || "")
          ),
        },
        onClick: e => onFaqDescriptionLinkClick(e, accordion),
      }),
    },

    CTA: accordion?.buttonLabel
      ? {
          text: accordion.buttonLabel,
          href: modifyHref(accordion.buttonHref),
          target: accordion.newTabButton === true ? "_blank" : "_self",
          trackingAttributes: {
            "data-datalayer-id": "kds-element-navigation",
            "data-datalayer-data": JSON.stringify({
              clickInternalLinks: accordion?.buttonhref ? "true" : "false",
              eventAction: `${accordion?.pageType}:${stripHTML(
                accordion?.title
              ).toLowerCase()}:${accordion.buttonLabel.toLowerCase()}`,
              eventMsg: "n/a",
              eventName: `${accordion?.pageType}:${stripHTML(
                accordion?.title
              ).toLowerCase()}:${accordion.buttonLabel.toLowerCase()}`,
              eventStatus: "n/a",
              eventType: "navigation",
              internalLinkName: accordion.buttonLabel.toLowerCase(),
              internalLinkPosition: `${accordion?.pageType}:button`,
              internalLinkType: `${accordion?.pageType}:${stripHTML(
                accordion?.title
              ).toLowerCase()}`,
              internalLinkURL:
                accordion.buttonhref && typeof window !== "undefined"
                  ? `${window.location.origin}${accordion.buttonHref
                      ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                      .join("")
                      .replace(".html", "")}`
                  : "n/a",
              internalLinkZoneName: `${accordion?.pageType}:${stripHTML(
                accordion?.title
              ).toLowerCase()}`,
            }),
          },
        }
      : null,

    CTALink: accordion?.ctaLabel
      ? {
          text: accordion.ctaLabel,
          href: modifyHref(accordion.cta),
          target: accordion.newTab === true ? "_blank" : "_self",
          trackingAttributes: {
            "data-datalayer-id": "kds-element-navigation",
            "data-datalayer-data": JSON.stringify({
              clickInternalLinks: accordion.cta ? "true" : "false",
              eventAction: `${accordion?.pageType}:${stripHTML(
                accordion?.title
              ).toLowerCase()}:${accordion.ctaLabel.toLowerCase()}`,
              eventMsg: "n/a",
              eventName: `${accordion?.pageType}:${stripHTML(
                accordion?.title
              ).toLowerCase()}:${accordion.ctaLabel.toLowerCase()}`,
              eventStatus: "n/a",
              eventType: "navigation",
              internalLinkName: accordion.ctaLabel.toLowerCase(),
              internalLinkPosition: `${accordion?.pageType}:teaser`,
              internalLinkType: `${accordion?.pageType}:${stripHTML(
                accordion?.title
              ).toLowerCase()}`,
              internalLinkURL:
                accordion.cta && typeof window !== "undefined"
                  ? `${window.location.origin}${accordion.cta
                      ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                      .join("")
                      .replace(".html", "")}`
                  : "n/a",
              internalLinkZoneName: `${accordion?.pageType}:${stripHTML(
                accordion?.title
              ).toLowerCase()}`,
            }),
          },
        }
      : null,

    singleExpansion,
    defaultExpandedIndices,
    topSpacer: accordionProps.topSpacer,
    bottomSpacer: accordionProps.bottomSpacer,

    accordions: itemsOrder.length
      ? itemsOrder
          .map(key => accordionItems[key])
          .filter(Boolean)
          .map(item => ({
            title: {
              text: item?.["cq:panelTitle"] || "",
            },
            children: React.createElement("div", {
              dangerouslySetInnerHTML: {
                __html: processDescriptionHrefs(
                  removeHtmlSuffixFromAnchors(item?.text || "")
                ),
              },
              onClick: e => onFaqDescriptionLinkClick(e, accordion),
            }),
          }))
      : null,
  }
}
